import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { ChevronDown, MapPinOff, Search, Trash2 } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { DestinationCard, DestinationRow, SiteThumb } from './destination-items';
import { CHIP } from './search-bar.styles';
import { useSearchBarContext } from './use-search-bar';

/**
 * The shortcut row above the card grid: recently searched destinations, falling
 * back to flagged ones when there's no history yet.
 *
 * History is the more valuable of the two — repeating a search is the single most
 * common reason someone reopens this picker — so it wins whenever it exists, and
 * trending only fills the gap for a first-time visitor. Renders nothing when
 * neither has entries, rather than leaving an empty labelled band.
 */
function QuickPicks() {
  const { index, siteValue, pickSite, history, clearHistory } = useSearchBarContext();

  const historySites = history.map((item) => index.byMappingCode(item.code)).filter((site) => site !== undefined);

  const isHistory = historySites.length > 0;
  const picks = isHistory ? historySites : index.trending;

  if (picks.length === 0) return null;

  return (
    <section>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold">{isHistory ? 'Gần đây' : 'Đang được tìm nhiều'}</h3>
        {isHistory && (
          <button
            type="button"
            onClick={clearHistory}
            aria-label="Xoá lịch sử tìm kiếm"
            className="text-muted-foreground hover:text-foreground focus-visible:ring-primary-ink rounded-md p-1 transition-colors focus-visible:ring-2 focus-visible:outline-none motion-reduce:transition-none"
          >
            <Trash2 className="size-4" />
          </button>
        )}
      </div>

      {/* Scrolls rather than wraps, so a long history can't push the cards off-screen. */}
      <div className="scrollbar-hide -mx-1 flex snap-x gap-2 overflow-x-auto scroll-pl-1 px-1 pb-0.5">
        {picks.map((site) => (
          <button
            key={site.mapping_code}
            type="button"
            data-selected={site.mapping_code === siteValue}
            onClick={() => pickSite(site)}
            className={CHIP.quickPick}
          >
            <SiteThumb site={site} className={CHIP.quickPickThumb} fallbackClassName="size-7 rounded-full text-[0.625rem]" />
            <span className={CHIP.quickPickLabel}>{site.name}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

interface DestinationPanelProps {
  /**
   * `popover` (desktop) focuses the search field on open and keeps every section
   * expanded — there is room. `sheet` (mobile) must not steal focus, or the
   * keyboard buries the cards, and collapses the full list behind a disclosure.
   * Mirrors DatePanel's variant so both panels read the same way.
   */
  variant: 'popover' | 'sheet';
  className?: string;
}

/**
 * The destination picker, shared by the desktop popover and the mobile sheet.
 *
 * It adapts to how many destinations there actually are. On a short list the grid
 * of cards *is* the complete list, so a search field and group headings would be
 * chrome over content the user can already see. Past the threshold those
 * affordances appear because the list stops being scannable. The quick-pick row
 * is common to both — a shortcut is worth having at any list length.
 */
export function DestinationPanel({ variant, className }: DestinationPanelProps) {
  const { index, siteValue, pickSite } = useSearchBarContext();
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const isSheet = variant === 'sheet';
  const allSectionId = useId();

  // Collapsed by default on the sheet: "Điểm đến nổi bật" above it already covers
  // the common picks, so the full list is the section worth folding away to keep
  // the date step reachable without scrolling.
  const [isAllOpen, setIsAllOpen] = useState(false);
  const isAllExpanded = !isSheet || isAllOpen;

  // Query lives here rather than in the shared state on purpose: typing then
  // re-renders only this component, never the form, the pill or the trigger.
  const results = useMemo(() => index.filter(query), [index, query]);
  const isSearching = query.trim().length > 0;

  useEffect(() => {
    if (isSheet) return;
    const timer = setTimeout(() => inputRef.current?.focus(), 60);
    return () => clearTimeout(timer);
  }, [isSheet]);

  const cards = (sites: typeof index.all) => (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      {sites.map((site) => (
        <DestinationCard key={site.mapping_code} site={site} isSelected={site.mapping_code === siteValue} onSelect={pickSite} />
      ))}
    </div>
  );

  if (index.all.length === 0) {
    return (
      <div className={cn('text-muted-foreground flex flex-col items-center justify-center gap-2 py-10', className)}>
        <MapPinOff className="size-6" />
        <p className="text-sm">Chưa có điểm đến nào</p>
      </div>
    );
  }

  // Short list: no search field or group headings — those tame a long list, and the
  // cards below are already the complete set. The quick-pick row still earns its
  // place, since re-picking a recent destination beats re-reading the grid.
  if (index.isShortList) {
    return (
      <div className={cn('flex flex-col gap-4', className)}>
        <QuickPicks />
        {cards(index.all)}
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      <div className="relative">
        <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
        <Input
          ref={inputRef}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Tìm cơ sở, địa điểm…"
          aria-label="Tìm điểm đến"
          className="rounded-chip pl-9"
        />
      </div>

      {isSearching ? (
        results.length > 0 ? (
          <div role="list" className="flex flex-col">
            {results.map((site) => (
              <div role="listitem" key={site.mapping_code}>
                <DestinationRow site={site} isSelected={site.mapping_code === siteValue} onSelect={pickSite} />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-muted-foreground flex flex-col items-center justify-center gap-2 py-10">
            <MapPinOff className="size-6" />
            <p className="text-sm">Không tìm thấy địa điểm phù hợp</p>
          </div>
        )
      ) : (
        <>
          <QuickPicks />

          <section>
            <h3 className="mb-2 text-sm font-semibold">Điểm đến nổi bật</h3>
            {cards(index.all)}
          </section>

          <section>
            {/* The heading stays an <h3> in both variants; on the sheet it simply
                wraps a button, so collapsing doesn't cost the document outline. */}
            <h3 className={cn('text-sm font-semibold', !isSheet && 'mb-2')}>
              {isSheet ? (
                <button
                  type="button"
                  onClick={() => setIsAllOpen((open) => !open)}
                  aria-expanded={isAllOpen}
                  aria-controls={allSectionId}
                  className="focus-visible:ring-primary-ink -mx-1 flex w-full cursor-pointer items-center gap-2 rounded-md px-1 py-1.5 text-left focus-visible:ring-2 focus-visible:outline-none"
                >
                  <span className="flex-1">Tất cả điểm đến</span>
                  {/* The count is what makes a collapsed section worth collapsing —
                      it says what's inside without needing to open it. */}
                  <span className="bg-pill text-muted-foreground rounded-chip px-2 py-0.5 text-xs font-medium">{index.all.length}</span>
                  <ChevronDown
                    aria-hidden="true"
                    className={cn(
                      'text-muted-foreground size-4 transition-transform duration-200 motion-reduce:transition-none',
                      isAllOpen && 'rotate-180',
                    )}
                  />
                </button>
              ) : (
                'Tất cả điểm đến'
              )}
            </h3>

            {isAllExpanded && (
              <div
                id={allSectionId}
                className={cn('grid grid-cols-2 gap-3', isSheet && 'animate-in fade-in slide-in-from-top-1 mt-2 duration-200 motion-reduce:animate-none')}
              >
                {index.groups.map((group) => (
                  <div key={group.key}>
                    {index.showGroupHeadings && (
                      <h4 className="text-muted-foreground mb-1 text-xs font-semibold tracking-wide uppercase">{group.key}</h4>
                    )}
                    <div role="list" className="flex flex-col">
                      {group.sites.map((site) => (
                        <div role="listitem" key={site.mapping_code}>
                          <DestinationRow site={site} isSelected={site.mapping_code === siteValue} onSelect={pickSite} />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
